import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";

// El panel no tiene rutas por sección, pero SÍ una ruta real por evento:
// /eventos-organizados/evento_XXX(/...). Se usa para detectar
// automáticamente "el usuario está viendo este evento ahora mismo" y
// mandarlo al servidor, que trae la configuración real de ese evento
// (nombre, fecha, modalidades, rangos de folios) para esa pregunta puntual.
const PATRON_RUTA_EVENTO = /^\/eventos-organizados\/(evento_[^/]+)/;

// ─────────────────────────────────────────────────────────────
// Configuración
// ─────────────────────────────────────────────────────────────

// Cambia esta URL por la de tu Firebase Function desplegada
// Ej: https://us-central1-dashport-prod.cloudfunctions.net/anthropicProxy
const PROXY_URL = process.env.REACT_APP_ANTHROPIC_PROXY_URL ||
    "https://us-central1-kmetasfirebase.cloudfunctions.net/anthropicProxy/";

// ─────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────

/**
 * DashportChat — widget de soporte con IA para el panel admin.
 *
 * Props:
 *   agentType: "organizador" | "atleta"  (default: "organizador")
 *   position:  "bottom-right" | "bottom-left"  (default: "bottom-right")
 *   eventId:   opcional — si el widget se monta dentro de un evento
 *              específico, se guarda junto con la conversación para poder
 *              filtrar/analizar por evento más adelante.
 *
 * Uso:
 *   <DashportChat agentType="organizador" />
 */
export default function DashportChat({
    agentType = "organizador",
    position = "bottom-right",
    eventId: eventIdProp = null,
}) {
    const location = useLocation();
    // Si se pasa eventId explícito por prop, gana ese; si no, se detecta
    // solo por la URL actual.
    const eventId = useMemo(() => {
        if (eventIdProp) return eventIdProp;
        const match = location.pathname.match(PATRON_RUTA_EVENTO);
        return match ? match[1] : null;
    }, [eventIdProp, location.pathname]);

    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // null mientras se confirma el feature flag, para no mostrar ni ocultar
    // el botón de golpe y evitar un parpadeo.
    const [habilitado, setHabilitado] = useState(null);
    const atleteAccount = useSelector((state) => state.atlete.atleteAccount);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);
    // Un conversationId estable por montaje del widget (una "sesión" de
    // chat) — se genera una sola vez, no en cada mensaje.
    const conversationIdRef = useRef(null);

    // Feature flag: apagado por defecto si no se puede leer, igual que en
    // el servidor (fail-closed) — así nunca se muestra el botón "prendido"
    // por accidente.
    useEffect(() => {
        const unsubscribe = firebase
            .firestore()
            .collection("appConfig")
            .doc("chatbot")
            .onSnapshot(
                (snap) => setHabilitado(snap.exists && snap.data().enabled === true),
                () => setHabilitado(false)
            );
        return unsubscribe;
    }, []);

    // Mensaje inicial al abrir el chat por primera vez
    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([
                {
                    role: "assistant",
                    content:
                        agentType === "organizador"
                            ? "¡Hola! Soy el asistente de Dashport 👋 Puedo ayudarte con el panel administrativo: configurar distancias, precios, códigos de descuento, cotizaciones y más. ¿En qué te ayudo?"
                            : "¡Hola! Soy el asistente de Dashport 👋 Puedo ayudarte con tu inscripción, correo de confirmación, entrega de kit y más. ¿En qué te ayudo?",
                },
            ]);
        }
    }, [open, agentType, messages.length]);

    // Auto-scroll al último mensaje
    useEffect(() => {
        if (open) {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, open]);

    // Focus al input cuando abre
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open]);

    function getOrCreateConversationId() {
        if (!conversationIdRef.current) {
            // .doc() sin argumento genera un id nuevo en el cliente sin
            // escribir nada todavía — mismo truco que .push().key en RTDB.
            conversationIdRef.current = firebase.firestore().collection("chatConversations").doc().id;
        }
        return conversationIdRef.current;
    }

    // Escribe el mensaje del usuario en base de datos ANTES de llamar a la
    // API — si la llamada falla, la pregunta ya quedó guardada.
    async function guardarMensajeUsuario(conversationId, uid, texto) {
        const db = firebase.firestore();
        const ahora = firebase.firestore.FieldValue.serverTimestamp();
        const conversacionRef = db.collection("chatConversations").doc(conversationId);

        const snap = await conversacionRef.get();
        if (!snap.exists) {
            await conversacionRef.set({
                userId: uid,
                eventId: eventId || null,
                agentType,
                startedAt: ahora,
                lastMessageAt: ahora,
                resolved: null,
            });
        } else {
            // No se tocan startedAt/resolved en mensajes siguientes de la
            // misma conversación.
            await conversacionRef.update({ lastMessageAt: ahora });
        }

        await db.collection("chatMessages").add({
            conversationId,
            userId: uid,
            eventId: eventId || null,
            role: "user",
            content: texto,
            createdAt: ahora,
            tokens: null,
            model: null,
            latencyMs: null,
            error: null,
            wasAnswered: null,
            feedback: null,
        });
    }

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        const userMessage = { role: "user", content: text };
        const newMessages = [...messages, userMessage];

        setMessages(newMessages);
        setInput("");
        setLoading(true);
        setError(null);

        try {
            const user = firebase.auth().currentUser;
            if (!user) throw new Error("Usuario no autenticado");
            const token = await user.getIdToken();

            const conversationId = getOrCreateConversationId();
            await guardarMensajeUsuario(conversationId, user.uid, text);

            // Solo envía los mensajes user/assistant a la API (no el saludo inicial del sistema)
            const apiMessages = newMessages.filter((m) => m.role !== "system");

            const res = await fetch(PROXY_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    messages: apiMessages,
                    agentType,
                    conversationId,
                    eventId,
                }),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Error del servidor");
            }

            const data = await res.json();
            setMessages([
                ...newMessages,
                {
                    role: "assistant",
                    content: data.response,
                    conversationId,
                    messageId: data.messageId,
                    accion: data.accion,
                },
            ]);
        } catch (err) {
            console.error("Error en DashportChat:", err);
            setError(err.message || "Hubo un problema al conectar con el asistente. Intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Requiere sesión iniciada y el feature flag activo.
    if (!atleteAccount || !habilitado) return null;

    const positionStyle = position === "bottom-left"
        ? { bottom: 24, left: 24 }
        : { bottom: 24, right: 24 };

    return (
        <>
            {/* ── Botón flotante ── */}
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Cerrar asistente" : "Abrir asistente Dashport"}
                style={{
                    position: "fixed",
                    ...positionStyle,
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "#1a1a2e",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                    zIndex: 9999,
                    transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.08)";
                    e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.35)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.25)";
                }}
            >
                {open ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                )}
            </button>

            {/* ── Ventana del chat ── */}
            {open && (
                <div
                    role="dialog"
                    aria-label="Asistente Dashport"
                    style={{
                        position: "fixed",
                        ...positionStyle,
                        bottom: position === "bottom-left" ? 88 : 88,
                        width: 360,
                        maxHeight: 520,
                        background: "#ffffff",
                        borderRadius: 16,
                        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        zIndex: 9998,
                        fontFamily: "'Segoe UI', system-ui, sans-serif",
                        fontSize: 14,
                    }}
                >
                    {/* Header */}
                    <div style={{
                        background: "#1a1a2e",
                        padding: "14px 18px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}>
                        <div style={{
                            width: 8, height: 8, borderRadius: "50%",
                            background: "#4ade80",
                            boxShadow: "0 0 6px #4ade80",
                        }} />
                        <div>
                            <div style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
                                Asistente Dashport
                            </div>
                            <div style={{ color: "#94a3b8", fontSize: 11 }}>
                                {agentType === "organizador" ? "Panel administrativo" : "Soporte atletas"}
                            </div>
                        </div>
                    </div>

                    {/* Mensajes */}
                    <div style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "14px 14px 8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                    }}>
                        {messages.map((msg, i) => (
                            <ChatBubble
                                key={i}
                                msg={msg}
                                isLast={i === messages.length - 1 && msg.role === "assistant"}
                            />
                        ))}

                        {/* Indicador de escritura */}
                        {loading && (
                            <div style={{ display: "flex", justifyContent: "flex-start" }}>
                                <div style={{
                                    padding: "9px 14px",
                                    background: "#f1f5f9",
                                    borderRadius: "14px 14px 14px 4px",
                                    display: "flex",
                                    gap: 4,
                                    alignItems: "center",
                                }}>
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} style={{
                                            width: 6, height: 6, borderRadius: "50%",
                                            background: "#94a3b8",
                                            animation: `bounce 1s ease-in-out ${i * 0.15}s infinite`,
                                        }} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                borderRadius: 8,
                                padding: "8px 12px",
                                color: "#dc2626",
                                fontSize: 12,
                            }}>
                                {error}
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    {/* Input */}
                    <div style={{
                        padding: "10px 12px",
                        borderTop: "1px solid #e2e8f0",
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-end",
                    }}>
                        <textarea
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Escribe tu pregunta..."
                            aria-label="Escribe tu pregunta"
                            rows={1}
                            style={{
                                flex: 1,
                                border: "1px solid #e2e8f0",
                                borderRadius: 10,
                                padding: "8px 12px",
                                fontSize: 13,
                                resize: "none",
                                outline: "none",
                                fontFamily: "inherit",
                                lineHeight: 1.5,
                                maxHeight: 100,
                                overflowY: "auto",
                                transition: "border-color 0.15s",
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#1a1a2e"}
                            onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
                            disabled={loading}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={loading || !input.trim()}
                            aria-label="Enviar pregunta"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 10,
                                background: loading || !input.trim() ? "#e2e8f0" : "#1a1a2e",
                                border: "none",
                                cursor: loading || !input.trim() ? "default" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                transition: "background 0.15s",
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                stroke={loading || !input.trim() ? "#94a3b8" : "#fff"}
                                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Animación bounce para el indicador de escritura */}
            <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
        </>
    );
}

// Burbuja de un mensaje. Las del asistente, cuando son la última de la
// conversación, incluyen el control de útil/no útil (feedback) — no tiene
// sentido pedir feedback de mensajes viejos que ya se dejaron atrás.
function ChatBubble({ msg, isLast }) {
    const [feedback, setFeedback] = useState(null);

    function enviarFeedback(valor) {
        setFeedback(valor);
        if (msg.messageId) {
            firebase
                .firestore()
                .collection("chatMessages")
                .doc(msg.messageId)
                .update({ feedback: valor })
                .catch(() => {});
        }
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
                maxWidth: "82%",
                padding: "9px 13px",
                borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                background: msg.role === "user" ? "#1a1a2e" : "#f1f5f9",
                color: msg.role === "user" ? "#fff" : "#1e293b",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            }}>
                {msg.content}
            </div>
            {msg.role === "assistant" && msg.accion && (
                // Navegación completa (no client-side routing) a propósito:
                // el destino solo resuelve su tab al montarse (?abrir=...),
                // así que hace falta un load real, no un history.push.
                <a
                    href={msg.accion.url}
                    style={{
                        fontSize: 12,
                        color: "#1a1a2e",
                        fontWeight: 600,
                        marginTop: 4,
                        textDecoration: "underline",
                    }}
                >
                    Ir directo a esta sección →
                </a>
            )}
            {isLast && msg.role === "assistant" && (
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <button
                        onClick={() => enviarFeedback("useful")}
                        aria-label="Esta respuesta fue útil"
                        aria-pressed={feedback === "useful"}
                        style={{
                            border: "none", background: "none", cursor: "pointer",
                            fontSize: 14, opacity: feedback === "useful" ? 1 : 0.4,
                        }}
                    >
                        👍
                    </button>
                    <button
                        onClick={() => enviarFeedback("not_useful")}
                        aria-label="Esta respuesta no fue útil"
                        aria-pressed={feedback === "not_useful"}
                        style={{
                            border: "none", background: "none", cursor: "pointer",
                            fontSize: 14, opacity: feedback === "not_useful" ? 1 : 0.4,
                        }}
                    >
                        👎
                    </button>
                </div>
            )}
        </div>
    );
}
